import React, { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Container,
  Card,
  CardBody,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
} from 'reactstrap';
import PageBreadcrumb from '../../../Components/Common/PageBreadcrumb';
import ActionButton from '../../../Components/Common/ActionButton';
import { DetailHeader } from '../../../Components/Common/Detail';
import { SkeletonCard } from '../../../Components/Common/Skeleton';
import classnames from 'classnames';
import notify from '../../../utils/notify';
import { confirmAction } from '../../../utils/confirm';
import AwardingTab from './AwardingTab';
import ActivityTab from './ActivityTab';
import useAwardingDetail from '../hooks/useAwardingDetail';
import useAwardingApi from '../hooks/useAwardingApi';
import {
  AWARDING_SENDER,
  AWARDING_STATUS,
  PROCUREMENT_TYPE_HEADER_LABEL,
  displayValue,
  getAwardingStatusConfig,
  getProcurementTypeLabel,
} from '../utils/awardingHelpers';
import StatusBadge, {
  colorToTone,
} from '../../../Components/Common/StatusBadge';

const TABS = [
  { id: 'awarding', label: 'Awarding', icon: 'ri-award-line' },
  { id: 'activity', label: 'Activity', icon: 'ri-history-line' },
];

const AwardingDetail = ({
  mode = 'evaluator',
  listPath = '/apps-awarding/view',
}) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { confirmDealRecap } = useAwardingApi();
  const [activeTab, setActiveTab] = useState('awarding');
  const [isConfirming, setIsConfirming] = useState(false);

  const {
    awarding,
    activities,
    responses,
    isLoading,
    isLoadingActivities,
    isLoadingResponses,
    refresh,
    refreshActivities,
    refreshResponses,
  } = useAwardingDetail(
    id,
    mode === 'supplier'
      ? AWARDING_SENDER.companyMitra
      : AWARDING_SENDER.pertamina
  );

  document.title = 'Awarding Detail';

  const header = useMemo(
    () => ({
      procurementNumber: displayValue(awarding?.procurementNumber),
      categoryLabel:
        awarding?.procurementTypeHeaderLabel ||
        PROCUREMENT_TYPE_HEADER_LABEL[awarding?.procurementTypeHeader] ||
        'Product Hydro',
      procTypeLabel:
        awarding?.procurementTypeLabel ||
        getProcurementTypeLabel(awarding?.procurementType),
      titleLabel: displayValue(awarding?.titleProcurement),
      status: awarding?.statusWording || 'Open',
    }),
    [awarding]
  );

  const statusConfig = getAwardingStatusConfig(header.status);

  const handleChanged = async () => {
    await refresh();
    await refreshActivities();
  };

  const handleRespondCreated = async () => {
    await refreshResponses();
    await refreshActivities();
  };

  const isSupplier = mode === 'supplier';
  // Konfirmasi hanya untuk sisi Pertamina; role DMUT / Registered Supplier tidak punya tombol ini.
  const canConfirm =
    !isSupplier && awarding?.status === AWARDING_STATUS.inProgress;

  const handleConfirm = async () => {
    const confirmed = await confirmAction({
      tone: 'primary',
      title: 'Confirm this awarding?',
      message:
        'The awarding is marked as confirmed and the deal recap can no longer be changed or removed. This cannot be undone.',
      subject: header?.procurementNumber,
      confirmLabel: 'Confirm awarding',
    });
    if (!confirmed) return;

    setIsConfirming(true);
    try {
      await confirmDealRecap(id);
      notify.success('Awarding confirmed successfully.');
      await handleChanged();
    } catch (error) {
      notify.apiError(error, 'confirm awarding');
    } finally {
      setIsConfirming(false);
    }
  };

  return (
    <React.Fragment>
      <div className='page-content'>
        <Container fluid>
          <PageBreadcrumb
            fallback={{ label: 'Awarding', to: listPath }}
            current='Detail'
            listClassName='p-3 py-2 bg-light'
          />

          {isLoading ? (
            <>
              <SkeletonCard lines={2} className='mb-3' />
              <SkeletonCard lines={6} />
            </>
          ) : !awarding ? (
            <Card className='shadow-none border'>
              <CardBody className='text-center py-5'>
                <i
                  className='ri-inbox-line text-muted'
                  style={{ fontSize: '3rem', opacity: 0.5 }}
                ></i>
                <p className='mt-2 mb-0 text-muted'>Awarding data not found</p>
              </CardBody>
            </Card>
          ) : (
            <>
              <DetailHeader
                onBack={() => navigate(listPath)}
                backLabel='Back to list'
                eyebrow={
                  <>
                    <i className='ri-hashtag' aria-hidden='true' />
                    <span>{header.procurementNumber}</span>
                    <span className='opacity-50'>|</span>
                    <span>{header.categoryLabel}</span>
                  </>
                }
                title={header.titleLabel}
                subtitle={header.procTypeLabel}
                badges={
                  <StatusBadge
                    tone={colorToTone(statusConfig.color)}
                    label={statusConfig.label}
                  />
                }
                actions={
                  canConfirm && (
                    <ActionButton
                      variant='primary'
                      icon='ri-check-line'
                      onClick={handleConfirm}
                      loading={isConfirming}
                    >
                      {isConfirming ? 'Confirming...' : 'Confirm'}
                    </ActionButton>
                  )
                }
                facts={[
                  {
                    icon: 'ri-building-line',
                    label: 'Awarded to',
                    value: awarding.companyMitraName,
                  },
                  {
                    icon: 'ri-user-line',
                    label: 'PIC',
                    value: awarding.picName,
                  },
                  {
                    icon: 'ri-mail-line',
                    label: 'Trading email',
                    value: awarding.tradingEmail,
                  },
                  {
                    icon: 'ri-message-3-line',
                    label: 'Responses',
                    value: String(responses.length),
                  },
                ]}
              />

              <div className='dt-tabs'>
                <Nav tabs className='nav-tabs-custom nav-primary'>
                  {TABS.map(tab => (
                    <NavItem key={tab.id}>
                      <NavLink
                        tag='button'
                        type='button'
                        className={classnames('cursor-pointer', {
                          active: activeTab === tab.id,
                        })}
                        onClick={() => setActiveTab(tab.id)}
                      >
                        <i className={`${tab.icon} align-middle me-1`}></i>
                        <span className='fw-semibold'>{tab.label}</span>
                      </NavLink>
                    </NavItem>
                  ))}
                </Nav>

                <TabContent activeTab={activeTab} className='p-3 p-md-4'>
                  <TabPane tabId='awarding'>
                    {activeTab === 'awarding' && (
                      <AwardingTab
                        awardingId={id}
                        awarding={awarding}
                        responses={responses}
                        isLoadingResponses={isLoadingResponses}
                        onChanged={handleChanged}
                        onRespondCreated={handleRespondCreated}
                        mode={mode}
                      />
                    )}
                  </TabPane>
                  <TabPane tabId='activity'>
                    {activeTab === 'activity' && (
                      <ActivityTab
                        activities={activities}
                        isLoading={isLoadingActivities}
                        onMount={refreshActivities}
                      />
                    )}
                  </TabPane>
                </TabContent>
              </div>
            </>
          )}
        </Container>
      </div>
    </React.Fragment>
  );
};

export default AwardingDetail;
